import React, { useState } from 'react';
import { useSprings, animated } from 'react-spring/web.cjs';
import { useDrag } from 'react-use-gesture';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { translations } from 'i18n';
import theme, { imageBorderRadius } from 'themes';
import ImageContainer from '../ImageContainer';

const IMAGE_HEIGHT = 160;
const EDGE_DISTANCE = 40;
const PREMIUM_BEZEL = 5;

const sizePlusBezel = (size, premiumIcon) =>
  size + (premiumIcon ? 2 * PREMIUM_BEZEL : 0);

const SliderContainer = styled.div`
  ${theme.SwiperV2};
  img {
    border-radius: ${imageBorderRadius};
  }
`;

const StyledSlider = styled.div`
  position: relative;
  height: ${IMAGE_HEIGHT}px;
`;

const ImageContainerStyle = `
  display: inline-block;
  overflow: hidden;
`;

const PremiumImageContainerStyle = (premiumIcon) => `
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${PREMIUM_BEZEL}px;
  ${
    premiumIcon
      ? `background: url(${premiumIcon}) center center / contain no-repeat;`
      : ''
  }
`;

const SingleImageContainerStyle = styled.div`
  margin: auto;
  text-align: center;
  ${theme.SwiperV2};
`;

const SlideImageStyle = `
  user-drag: none;
`;

const ItemContainer = styled(animated.div)`
  position: absolute;
  will-change: transform;
`;

const SwiperPanel = ({ banners = [], premiumIcon }) => {
  const {
    component: {
      AppDetails: { bannerAltText }
    }
  } = translations;

  const bannerWidth =
    banners.length > 0
      ? sizePlusBezel(
          (IMAGE_HEIGHT / banners[0].height) * banners[0].width,
          premiumIcon
        )
      : 0;
  const itemWidth = bannerWidth + EDGE_DISTANCE;
  const [active, setActive] = useState(0);
  const [props, set] = useSprings(banners.length, (i) => ({
    x: EDGE_DISTANCE + itemWidth * i
  }));

  const bind = useDrag(
    ({ down, movement: [mx], direction: [xDir], distance }) => {
      let newActive = active;
      if (!down && distance >= itemWidth / 2) {
        const distanceCount = Math.round(distance / itemWidth);
        setActive((active) => {
          newActive = Math.max(
            0,
            Math.min(
              active + (xDir > 0 ? -distanceCount : distanceCount),
              banners.length - 1
            )
          );
          return newActive;
        });
      }

      set((i) => ({
        x: EDGE_DISTANCE + itemWidth * (i - newActive) + (down ? mx : 0),
        immediate: down
      }));
    }
  );

  if (banners.length === 1) {
    const [banner] = banners;
    return (
      <SingleImageContainerStyle
        style={{ width: bannerWidth }}
        aria-label="swiper-panel"
      >
        <ImageContainer
          containerAttr={{
            width: sizePlusBezel(banner.width, premiumIcon),
            height: sizePlusBezel(banner.height, premiumIcon),
            styles: premiumIcon
              ? PremiumImageContainerStyle(premiumIcon)
              : {
                  display: 'inline-block'
                }
          }}
          imageAttr={{
            alt: bannerAltText(),
            src: banner.uri,
            styles: SlideImageStyle
          }}
        />
      </SingleImageContainerStyle>
    );
  }

  return (
    <SliderContainer>
      <StyledSlider {...bind()}>
        {banners.map((banner, index) => (
          <ItemContainer
            key={index}
            style={{
              transform: props[index].x.interpolate(
                (x) => `translate3d(${x}px, 0, 0)`
              ),
              width: bannerWidth
            }}
          >
            <ImageContainer
              containerAttr={{
                width: sizePlusBezel(banner.width, premiumIcon),
                height: sizePlusBezel(banner.height, premiumIcon),
                styles: premiumIcon
                  ? PremiumImageContainerStyle(premiumIcon)
                  : ImageContainerStyle
              }}
              imageAttr={{
                alt: bannerAltText(),
                src: banner.uri,
                styles: SlideImageStyle
              }}
            />
          </ItemContainer>
        ))}
      </StyledSlider>
    </SliderContainer>
  );
};

SwiperPanel.propTypes = {
  banners: PropTypes.arrayOf(
    PropTypes.shape({
      uri: PropTypes.string.isRequired
    })
  ).isRequired
};

export default SwiperPanel;
